import {
  Component,
  OnInit,
  OnDestroy,
  HostListener,
  Output,
  EventEmitter,
  Input,
  PLATFORM_ID,
  inject,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatBadgeModule } from '@angular/material/badge';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatDividerModule } from '@angular/material/divider';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { filter, Subject, takeUntil } from 'rxjs';
import { InteractionStatus } from '@azure/msal-browser';
import { TranslateModule } from '@ngx-translate/core';
import { NotificationService } from '../../../services/notification-service';
import { AuthService } from '../../../services/auth-service';
import { INotificationItem } from '../../../interfaces/notification-interface';
import { IProfileDetail } from '../../../interfaces/profile-interface';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatBadgeModule,
    MatSidenavModule,
    MatListModule,
    MatDividerModule,
    TranslateModule,
  ],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Output() toggleSidebar = new EventEmitter<void>();
  @Input() isSidebarCollapsed = false;

  isMobile = false;
  notificationCount = 0;
  notifications: INotificationItem[] = [];
  profile: IProfileDetail | null = null;
  userName = '';
  private readonly _destroying$ = new Subject<void>();
  private platformId = inject(PLATFORM_ID);
  constructor(
    private router: Router,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private notificationService: NotificationService,
    private authService: AuthService,
  ) {}

  ngOnInit() {
    this.checkScreenSize();
    this.initializeService();
  }

  private async initializeService(): Promise<void> {
    await this.msalService.instance.initialize();

    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None,
        ),
        takeUntil(this._destroying$),
      )
      .subscribe(async () => {
        this.checkAndSetUser();
      });

    this.authService.profileSubject$
      .pipe(takeUntil(this._destroying$))
      .subscribe((profile) => {
        this.profile = profile?.userMail ? profile : null;
      });
  }

  get authenticated(): boolean {
    return this.msalService.instance.getActiveAccount() ? true : false;
  }
  checkAndSetUser() {
    const accounts = this.msalService.instance.getAllAccounts();
    if (this.authenticated) {
      this.userName =
        this.msalService.instance.getActiveAccount()?.name ?? 'User';
    } else if (accounts && accounts?.length) {
      this.userName = accounts[0]?.name ?? 'User';
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.checkScreenSize();
  }

  checkScreenSize() {
    if (isPlatformBrowser(this.platformId)) {
      this.isMobile = window.innerWidth < 768;
    }
  }

  onToggleSidebar() {
    this.toggleSidebar.emit();
  }

  navigateTo(route: string) {
    this.router.navigate([route]);
  }

  handleNotificationClick(notification: INotificationItem) {
    notification.isRead = true;
    this.updateNotificationCount();
    // Navigate to notification center with selected notification ID
    this.router.navigate(['/notifications'], {
      queryParams: { notificationId: notification.id },
    });
  }

  markAllAsRead() {
    this.notifications.forEach((notification) => (notification.isRead = true));
    this.updateNotificationCount();
  }

  viewAllNotifications() {
    this.router.navigate(['/notifications']);
  }

  private updateNotificationCount() {
    this.notificationCount = this.notifications.filter((n) => !n.isRead).length;
  }

  getNotificationIcon(notification: INotificationItem): string {
    // Return appropriate icon based on notification type or default
    return 'notifications';
  }

  formatNotificationTime(dateString: string): string {
    if (!dateString) return '';

    const notificationDate = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - notificationDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60)
      return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24)
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

    return notificationDate.toLocaleDateString();
  }

  // Implement logout logic

  logout() {
    this.msalService.logoutRedirect();
  }

  ngOnDestroy() {
    this._destroying$.next();
    this._destroying$.complete();
  }
}
